/* eslint-disable complexity */
import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
	Box,
	Button,
	Checkbox,
	CircularProgress,
	FormControlLabel,
	IconButton,
	MenuItem,
	Typography,
} from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { authService, paymentService, stripeService } from '@/modules/_core/services';
import ExternalLink from '@/modules/_core/components/links/External-link';
import { Elements, ElementsConsumer, PaymentElement } from '@stripe/react-stripe-js';
import {
	FileVariantsEnum,
	GetPricesAndFeesDto,
	OrderDto,
	priceFormatter,
	UserDto,
	waitAsync,
	VerificationInstance,
	EventBundleDto,
	getStripeFutureUsage,
	makeOrderPageLink,
} from '@sweatpals/shared';
import { getPublicFileUrl } from '@/modules/_core/utils/file.utils';
import { useSnackbar } from 'notistack';
import {
	clsx,
	getReponseDataFromAxiosError,
	HostBackground,
	IntakeFormResult,
	Showable,
	useStyles,
	OrderSummary,
	useDebounce,
} from '@sweatpals/react-ui-kit';
import { isEmail, isPhoneNumber } from 'class-validator';
import { CheckRounded } from '@mui/icons-material';
import Image from 'next/legacy/image';
import * as yup from 'yup';
import { membershipFormStyles } from './event-bundle-form.styles';
import RemoteImage from '@/modules/_core/components/images/Remote-image';
import { ArrowBackIcon, CheckIcon, EditIcon } from '@/modules/_core/icons';
import loader from '@/public/assets/images/loader.gif';
import { CoreTextField } from '@/modules/_core/components/_ui/core-textfield';
import { CorePhoneInput } from '@/modules/_core/components/_ui/core-phone-input';
import { CoreFormControl } from '@/modules/_core/components/_ui/core-form-control';
import { StripeElements, PaymentIntentConfirmParams } from '@stripe/stripe-js';
import { flatten, isEmpty, reduce } from 'lodash-es';
import { useFormik } from 'formik';
import { AxiosError } from 'axios';
import { TYPEFORM_URL, HOME_PAGE_URL } from '@/modules/_core/constants';
import { CoreSelect } from '@/modules/_core/components/_ui/core-select';
import { CoreButtonBase } from '@/modules/_core/components/_ui/core-button';
import ReCaptchaBackgroundVerifier from '@/modules/_core/components/re-captcha/re-captcha-background.component';
import { defaultHostColorHex } from '@/modules/host/pages/host/host-page.styles';
import UserAvatar from '@/modules/_core/components/images/UserAvatar';
import { EventBundleCard } from '../event-bundle-card/event-bundle-card.component';
import { defaultStripeElementsOptions } from '@sweatpals/react-ui-kit';
import { commonCheckoutFormStyles } from '../checkout-form/checkout-form.styles';
import { PaymentElements } from '../checkout-form/payment-elements.component';

enum CheckoutStepEnum {
	Form = 1,
	VerifyCode = 2,
}

export interface EventBundleCheckoutFormDto {
	fullName: string;
	email: string;
	phone: string;
	code?: string;
	recaptchaTokenV2?: string;
	recaptchaTokenV3?: string;
}

export interface EventBundleFormProps {
	hostData: UserDto;
	hostEventBundles: EventBundleDto[];
	preselectedEventBundleId?: string;
	isEventBundleSelectDisabled?: boolean;
	Header?: FC<{ clearSelectedEventBundleId(): void }>;
	onSubmit?(order: OrderDto): Promise<void>;
}

export const EventBundleForm: FC<EventBundleFormProps> = (props) => {
	const {
		hostData,
		Header,
		hostEventBundles,
		preselectedEventBundleId,
		isEventBundleSelectDisabled = false,
		onSubmit,
	} = props;

	const [hasEventBundle, setHasEventBundle] = useState(false);
	const [checkoutStep, setCheckoutStep] = useState(CheckoutStepEnum.Form);
	const [isPolicyAgreed, setIsPolicyAgreed] = useState(true);
	// const [isConsentAgreed, setIsConsentAgreed] = useState(true);
	const [isEventBundleEditable, setIsEventBundleEditable] = useState(false);
	const [resendCodeTimer, setResetCodeTimer] = useState<number | null>(null);
	// const isPolicyAndConsentAgreed = useMemo(() => isPolicyAgreed && isConsentAgreed, [isPolicyAgreed, isConsentAgreed]);

	const [isSignIn, setIsSignIn] = useState<boolean | null>(null);
	const [verification, setVerification] = useState<null | VerificationInstance>(null);
	const [priceAndFees, setPriceAndFees] = useState<GetPricesAndFeesDto | null>(null);
	const [isPaying, setIsPaying] = useState(false);
	const [validateOnChange, setValidateOnChange] = useState(false);
	const [showRecaptchaCheckbox, setShowRecaptchaCheckbox] = useState(false);
	const [selectedEventBundleId, setSelectedEventBundleId] = useState<string | null>(preselectedEventBundleId ?? null);

	const { debounceFunc, cancelFunc } = useDebounce(1000);
	const { enqueueSnackbar } = useSnackbar();
	const formWrapperRef = useRef<HTMLDivElement | null>(null);
	const bodyRef = useRef<HTMLDivElement | null>(null);

	const styles = useStyles(membershipFormStyles);
	const commonStyles = useStyles(commonCheckoutFormStyles);

	const publicHostEventBundles = useMemo(() => hostEventBundles ?? [], [hostEventBundles]);
	const primaryColor = useMemo(() => hostData?.primaryColor ?? defaultHostColorHex, [hostData?.primaryColor]);

	const selectedEventBundle = useMemo(
		() => selectedEventBundleId && hostEventBundles.find(eventBundle => eventBundle.id === selectedEventBundleId),
		[hostEventBundles, selectedEventBundleId]
	);

	const paymentMethodTypes = useMemo(() => {
		const methods = ['card'];

		if (!selectedEventBundle) return methods;

		if (selectedEventBundle?.additionalData?.allowKlarnaPayments) {
			methods.push('klarna');
		}

		return methods;
	}, [selectedEventBundle]);

	const {
		isLoading: isPaymentIntentLoading,
		mutateAsync: createPaymentIntent,
	} = useMutation(
		async (data: EventBundleCheckoutFormDto) => {
			if (!selectedEventBundleId) {
				return;
			}

			return paymentService.createPublicPaymentIntentV2({
				...data,
				supportedPaymentMethodTypes: paymentMethodTypes,
				eventBundleId: selectedEventBundleId,
				code: checkoutStep === CheckoutStepEnum.VerifyCode ? data.code : undefined,
			})
		},
		{
			onError: (err: Error) => {
				const data = getReponseDataFromAxiosError(err);

				if (data.statusCode === 409) {
					setHasEventBundle(true);
					return;
				}

				if (err instanceof AxiosError && err.response?.status === 403) {
					setShowRecaptchaCheckbox(err.response?.status === 403);
				}

				const msg = data?.message || err.message;
				enqueueSnackbar(msg, { variant: 'error' });
			},
		}
	);

	const {
		data: newPriceAndFees,
		isLoading: isPriceAndFeesLoading,
		mutateAsync: getPriceAndFees,
	} = useMutation(
		async (data: { eventBundleId: string }) => {
			const priceAndFees = await paymentService.getPublicPrices(data);
			return priceAndFees;
		},
		{
			onError: (err: Error) => {
				const data = getReponseDataFromAxiosError(err);

				enqueueSnackbar(data?.message || err.message, { variant: 'error' });
			},
		}
	);

	useEffect(() => {
		if (newPriceAndFees && !isPriceAndFeesLoading) {
			setPriceAndFees(newPriceAndFees);
		}
	}, [newPriceAndFees, isPriceAndFeesLoading]);

	useEffect(() => {
		if (selectedEventBundleId) {
			getPriceAndFees({ eventBundleId: selectedEventBundleId });
		}
	}, []);

	const onSendVerificationCode = useCallback(async (phone: string) => {
		try {
			const { verification, isSignIn } = await authService.signInOrSignUpWithPhone(phone);

			setVerification(verification);
			setIsSignIn(isSignIn);
			setResetCodeTimer(60);
		} catch (e) {
			if (e instanceof AxiosError) {
				enqueueSnackbar(e.response?.data?.message || e.message);
			}
		}
	}, [enqueueSnackbar]);

	const getOrder = useCallback(async (orderId: string): Promise<OrderDto | undefined> => {
		try {
			let order: OrderDto | undefined;
			let attempts = 0;

			while (!order && attempts <= 10) {
				attempts++;
				await waitAsync(5000);
				order = await paymentService.getOrderByIdOrPaymentIntentId(orderId);
			}

			return order;
		} catch (e) {
			if (e instanceof AxiosError) {
				enqueueSnackbar(e.response?.data?.message || e.message, { variant: 'error' });
			}
		}
	}, [enqueueSnackbar]);

	const onEditEventBundleButtonClick = useCallback((): void => {
		if (isEventBundleEditable) {
			cancelFunc();
			getPriceAndFees({
				eventBundleId: selectedEventBundleId,
			});
		}

		setIsEventBundleEditable(!isEventBundleEditable);
	}, [cancelFunc, getPriceAndFees, isEventBundleEditable, selectedEventBundleId]);

	const validationSchema = useMemo(() => {
		const validationSchema: Record<string, yup.Schema> = {
			fullName: yup.string().required('First name is required')
				.min(3, 'Full name is too short'),
			email: yup.string().required('Email is required')
				.test({
					message: 'Invalid email',
					test: value => isEmail(value),
				}),
			phone: yup.string().required('Phone number is required')
				.test({
					message: 'Invalid phone number',
					test: value => isPhoneNumber(value, null),
				}),
		};

		return yup.object().shape(validationSchema);
	}, []);

	const {
		values,
		errors,
		handleChange,
		setFieldValue,
		isSubmitting,
		validateField,
		validateForm,
		setValues,
	} = useFormik<EventBundleCheckoutFormDto>({
		initialValues: {
			fullName: '',
			email: '',
			phone: '+1',
			recaptchaTokenV2: undefined,
			recaptchaTokenV3: undefined,
		},
		validateOnChange,
		validationSchema,
		onSubmit: undefined,
	});

	const handleSetRecaptchaV2Token = useCallback(
		(token: string | null) => {
			setFieldValue('recaptchaTokenV2', token);
			setFieldValue('recaptchaTokenV3', null);
		},
		[setFieldValue]
	);

	const handleSetRecaptchaV3Token = useCallback(
		(token: string | null) => {
			setFieldValue('recaptchaTokenV3', token);
			setFieldValue('recaptchaTokenV2', null);
		},
		[setFieldValue]
	);

	const handleChangePhone = useCallback((phone: string): void => {
		setFieldValue('phone', phone);
	}, [setFieldValue]);

	const onBackButtonClick = useCallback(() => {
		setVerification(null);
		setFieldValue('code', '');
		setCheckoutStep(CheckoutStepEnum.Form);
		formWrapperRef.current?.scrollTo(0, 0);
		bodyRef.current?.scrollTo(0, 0);
	}, [setFieldValue]);

	const handlePay = useCallback(async (elements: StripeElements, selectedPaymentMethodType: string): Promise<void> => {
		setIsPaying(true);

		try {
			const errors = await validateForm();

			if (!isEmpty(errors)) {
				return;
			}

			setValidateOnChange(false);
			const paymentIntent = await createPaymentIntent(values);

			if (paymentIntent.intent) {
				const response = await stripeService.handleConfirmPayment(
					selectedPaymentMethodType,
					elements,
					paymentIntent,
					values,
				);

				if (!response) {
					enqueueSnackbar('Payment failed', { variant: 'error' });
					return;
				}

				if (response.error) {
					enqueueSnackbar(response.error.message, { variant: 'error' });

					return;
				}

				if (response.paymentIntent.status === 'succeeded') {
					const order = await getOrder(paymentIntent.orderId);

					if (order) {
						await onSubmit?.(order);
						setSelectedEventBundleId(null);
					}
				}
			}
		} finally {
			setIsPaying(false);
		}
	}, [createPaymentIntent, enqueueSnackbar, getOrder, onSubmit, validateForm, values]);

	return (
		<Box sx={styles.root}>
			<HostBackground
				background={
					hostData?.backgroundId &&
					getPublicFileUrl(hostData.backgroundId, {
						variant: FileVariantsEnum.L,
					})
				}
				primaryColor={hostData.primaryColor}
			/>

			<Box sx={styles.content}>
				{Header && (
					<Header
						clearSelectedEventBundleId={() => {
							setSelectedEventBundleId(null);
						}}
					/>
				)}

				<Showable show={hasEventBundle}>
					<Box display="flex" flex={1} justifyContent="center" alignItems="center" overflow="hidden">
						<Box sx={styles.placeholder}>
							<RemoteImage
								src={hostData.avatarId}
								alt="host avatar"
								width={50}
								height={50}
								sx={{ borderRadius: '50%', margin: '0 auto' }}
							/>
							<Box display="flex" flexDirection="column" gap="32px">
								<Typography sx={styles.placeholderTitle}>
									There's already a classpack associated with this number and email
								</Typography>
								<Typography sx={styles.placeholderDescription}>
									To cancel your classpack, please email us at hello@sweatpals.com.
									After cancellation, you will still be able to use your classpack until its expiration date,
									but no further charges will be applied. We typically answer within 24h.
								</Typography>
								<Box
									display="flex"
									justifyContent="center"
									alignItems="center"
									flexDirection={{ xs: 'column', md: 'row' }}
									gap="25px"
								>
									<Showable show={hasEventBundle}>
										<CoreButtonBase
											variant="secondary"
											sx={{ width: 'fit-content' }}
											onClick={() => {
												onBackButtonClick();
												setHasEventBundle(false);
											}}
										>
											try a different number and email
										</CoreButtonBase>
									</Showable>
									<ExternalLink href={TYPEFORM_URL}>
										<CoreButtonBase variant="primary" sx={{ width: 'fit-content' }}>
											Contact support
										</CoreButtonBase>
									</ExternalLink>
								</Box>
							</Box>
						</Box>
					</Box>
				</Showable>

				<Showable hide={hasEventBundle}>
					{selectedEventBundleId ? (
						<Box sx={styles.body} ref={bodyRef}>
							<Box sx={styles.membershipFormLeftPart}>
								<Box p="24px" display="flex" alignItems="center" gap="12px">
									<RemoteImage
										src={hostData.avatarId}
										alt="host avatar"
										width={50}
										height={50}
										sx={{ borderRadius: '50%' }}
									/>

									<Typography sx={styles.membershipInfoHost}>{hostData.fullName}</Typography>
								</Box>
								<Box position="relative" zIndex={1}>
									<svg
										style={{
											height: '1px',
											width: '100%',
											position: 'absolute',
											bottom: 0,
											left: 0,
											strokeWidth: '1px',
										}}
									>
										<line x1="0" y1="0" x2="100%" y2="0" stroke="#FFFFFF1A" strokeDasharray="6 6" />
									</svg>
									<Button
										sx={styles.editMembershipButton}
										disabled={isPriceAndFeesLoading && !isEventBundleEditable}
										onClick={async (): Promise<void> => {
											await onEditEventBundleButtonClick();
										}}
									>
										{isEventBundleEditable ? <CheckIcon /> : <EditIcon />}
										{isEventBundleEditable ? 'Save' : 'Edit'}
									</Button>
								</Box>
								<Box sx={styles.orderSummary}>
									<Showable show={isPriceAndFeesLoading && !isEventBundleEditable}>
										<Box sx={styles.loader}>
											<Image src={loader} width={64} height={64} alt="order-louder" />
										</Box>
									</Showable>
									<Box
										sx={{
											height: { xs: 'auto', md: '100%' },
											overflow: { xs: 'unset', md: 'auto' },
											p: '0 12px 1px',
											opacity: isPriceAndFeesLoading && !isEventBundleEditable ? 0.2 : 1,
										}}
									>
										{isEventBundleEditable ? (
											<Box display="flex" flexDirection="column" gap="8px">
												{selectedEventBundle ? (
													<Box display="flex" alignItems="center" justifyContent="space-between">
														<Box display="flex" alignItems="flex-start" gap="4px">
															<Typography sx={styles.membershipTierAmount}>
																{priceFormatter(selectedEventBundle.price)}
															</Typography>
														</Box>
													</Box>
												) : null}
												<CoreSelect
													disabled={isEventBundleSelectDisabled}
													value={selectedEventBundle?.id}
													renderValue={() => selectedEventBundle?.name}
													onChange={(e) => {
														const eventBundleId = e.target.value;
														const eventBundle = hostEventBundles.find((eb) => eb.id === eventBundleId);

														if (eventBundle) {
															setSelectedEventBundleId(eventBundle.id);
														}
													}}
													paperSx={{
														'& .MuiList-root': {
															'& .MuiMenuItem-root': {
																justifyContent: 'space-between',
															},
														},
													}}
													sx={{
														'& .MuiSelect-select': {
															p: '10px 16px',
														},

														'& .MuiSelect-icon': {
															display: isEventBundleSelectDisabled ? 'none' : 'block',
														}
													}}
												>
													{hostEventBundles.map((eventBundle) => (
														<MenuItem key={eventBundle.id} value={eventBundle.id}>
															<Typography variant="inherit">{eventBundle.name}</Typography>
															<Showable show={eventBundle.id === selectedEventBundle?.id}>
																<CheckIcon />
															</Showable>
														</MenuItem>
													))}
												</CoreSelect>
												<Typography sx={styles.membershipDescription}>{selectedEventBundle?.description}</Typography>
											</Box>
										) : (
											<OrderSummary
												eventBundle={selectedEventBundle}
												priceAndFees={priceAndFees}
											/>
										)}
									</Box>
								</Box>
							</Box>

							<Box sx={styles.membershipFormRightPart} ref={formWrapperRef}>
								<Box sx={styles.form} component="form">
									<Showable show={checkoutStep === CheckoutStepEnum.VerifyCode}>
										<IconButton sx={styles.backButton} disabled={isSubmitting} onClick={onBackButtonClick}>
											<ArrowBackIcon />
										</IconButton>
									</Showable>
									<Showable show={checkoutStep === CheckoutStepEnum.Form}>
										<Box display="flex" flexDirection="column" gap="24px">
											<Typography sx={styles.title}>Your info</Typography>
											<CoreTextField
												controlId="fullName"
												id="fullName-input"
												value={values.fullName}
												name="fullName"
												label="Full name"
												placeholder="Full name"
												requiredMark
												disabled={isSubmitting || isPaying}
												onChange={handleChange}
												onBlur={() => {
													validateField('fullName');
												}}
												error={!!errors.fullName}
												helperText={errors.fullName}
											/>
											<CoreTextField
												controlId="email"
												id="email-input"
												value={values.email}
												name="email"
												label="Email"
												placeholder="Enter email"
												requiredMark
												disabled={isSubmitting || isPaying}
												onChange={handleChange}
												onBlur={() => {
													validateField('email');
												}}
												error={!!errors.email}
												helperText={errors.email}
											/>
											<CorePhoneInput
												controlId="phone"
												id="phone-input"
												placeholder="Phone Number"
												label="Phone Number"
												requiredMark
												name="phone"
												disabled={isSubmitting || isPaying}
												value={values.phone}
												defaultCountry="US"
												forceCallingCode
												MenuProps={{ disableAutoFocusItem: true }}
												onChange={handleChangePhone}
												onBlur={() => {
													validateField('phone');
												}}
												error={!!errors.phone}
												helperText={errors.phone}
											/>
											<Box display="flex" alignItems="center" justifyContent="space-between">
												<Typography sx={styles.title}>Payment</Typography>
											</Box>
											<Showable show={(priceAndFees?.fullPrice ?? 0) > 0}>
												<Box position="relative">
													<Elements
														stripe={stripeService.stripe}
														options={{
															...defaultStripeElementsOptions({
																currency: priceAndFees?.currency,
																amount: priceAndFees?.fullPrice ?? 50,
																onBehalfOf: selectedEventBundle?.community?.stripeConnectedAccountId,
																paymentMethodTypes: paymentMethodTypes.filter(Boolean),
																setupFutureUsage: getStripeFutureUsage({ event: true }),
															}),
														}}
													>
														<ElementsConsumer>
															{({ elements }) => (
																<PaymentElements
																	commonStyles={commonStyles}
																	elements={elements}
																	values={values}
																	isPolicyAgreed={isPolicyAgreed}
																	isPaying={isPaying}
																	isPriceAndFeesLoading={isPriceAndFeesLoading}
																	handlePay={handlePay}
																	ticketsQuantity={1}
																/>
															)}
														</ElementsConsumer>
													</Elements>
													<Showable show={isPaying}>
														<Box sx={styles.loader}>
															<Image src={loader} width={64} height={64} alt="stripe-louder" />
														</Box>
													</Showable>
												</Box>
											</Showable>

											<Box>
												<ReCaptchaBackgroundVerifier
													showCheckbox={showRecaptchaCheckbox}
													onVerifyV2={handleSetRecaptchaV2Token}
													onVerifyV3={handleSetRecaptchaV3Token}
												/>
											</Box>
										</Box>
									</Showable>
									<Showable show={checkoutStep === CheckoutStepEnum.VerifyCode}>
										<Box display="flex" flexDirection="column" gap="8px" mb="24px">
											<Typography sx={styles.verificationTitle}>Confirm phone number</Typography>
											<Typography sx={styles.verificationDescription}>
												{`Enter code that we have sent you on `}
												<Box component="span" fontWeight={600}>
													{values.phone}
												</Box>
											</Typography>
										</Box>
										<Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
											<CoreTextField
												fullWidth
												placeholder="Code"
												disabled={isSubmitting}
												value={values.code}
												name="code"
												autoComplete="one-time-code"
												onChange={handleChange}
												error={!!errors.code}
												helperText={errors.code}
											/>
											<Typography
												sx={{
													fontFamily: 'Montserrat',
													fontStyle: 'normal',
													fontWeight: 400,
													fontSize: '13px',
													lineHeight: '16px',
													color: '#FFFFFF',
												}}
											>
												{resendCodeTimer ? (
													<Box component="span">Resend in {resendCodeTimer}.</Box>
												) : (
													<Box
														component="span"
														sx={
															!isSubmitting
																? {
																		'&:hover': {
																			cursor: 'pointer',
																			textDecoration: 'underline',
																		},
																	}
																: undefined
														}
														onClick={() => {
															if (!isSubmitting) {
																onSendVerificationCode(values.phone);
															}
														}}
													>
														Resend code
													</Box>
												)}
											</Typography>
										</Box>
									</Showable>
								</Box>
								<Showable show={checkoutStep === CheckoutStepEnum.Form}>
									<Box sx={{ mt: '16px', display: 'flex', justifyContent: 'flex-start', px: '10px' }}>
										<FormControlLabel
											sx={styles.checkBox}
											control={
												<Checkbox
													id="privacy-policy"
													disabled={isSubmitting || isPaying}
													checked={isPolicyAgreed}
													onChange={() => {
														setIsPolicyAgreed(!isPolicyAgreed);
													}}
													checkedIcon={<CheckRounded />}
												/>
											}
											label={
												<>
													By continuing, you agree to our{' '}
													<a href="/privacy-policy.html" target="_blank">
														Privacy Policy
													</a>{' '}
													and{' '}
													<a href="/terms.html" target="_blank">
														Terms & Conditions
													</a>
												</>
											}
										/>
									</Box>
									{/* Note -  removing this on this (RD-3956) requerst, might need it later */}
									{/* <Box sx={{ mt: '16px', display: 'flex', justifyContent: 'flex-start', px: '10px' }}>
										<FormControlLabel
											sx={styles.checkBox}
											control={
												<Checkbox
													id="consent"
													disabled={isSubmitting || isPaying}
													checked={isConsentAgreed}
													onChange={() => { setIsConsentAgreed(!isConsentAgreed); }}
													checkedIcon={<CheckRounded />}
												/>
											}
											label={<>
												By continuing, I provide my electronic signature authorising {hostData?.fullName ?? 'SweatPals'}{' '}
												to send me texts at the phone number on my account and emails,{' '}
												including via automated technology. I may withdraw consent at any time by replying STOP.
											</>}
										/>
									</Box> */}
								</Showable>
							</Box>
						</Box>
					) : (
						<Box sx={styles.selectMembership}>
							<Box
								sx={clsx(styles.selectMembershipBody, {
									width: '100%',
									maxWidth: publicHostEventBundles?.length === 1 ? '634px' : 'unset'
								})}
							>
								<Box display="flex" flexDirection="column" gap="16px">
									<UserAvatar user={hostData} sx={{ width: '49px', height: '49px' }} />
									<Typography sx={styles.selectMembershipTitle}>
										{`Explore ${hostData.fullName}’s Membership Plans`}
									</Typography>
								</Box>
								<Box sx={styles.selectMembershipContent}>
									<Box sx={styles.membershipsCards}>
										{publicHostEventBundles
											.map((eventBundle, _idx) => (
												<EventBundleCard
													key={eventBundle.id}
													hostPrimaryColorHex={primaryColor}
													eventBundle={eventBundle}
													onGetEventBundle={(eventBundleId) => {
														setSelectedEventBundleId(eventBundleId);
														cancelFunc();
														getPriceAndFees({
															eventBundleId,
														});
													}}
													sx={{
														width: publicHostEventBundles?.length === 1 ? '100%' : '380px',
														maxWidth: 'unset',
														height: 'fit-content',
													}}
												/>
											))}
									</Box>
									<Typography sx={styles.selectMembershipNote}>
										{`Your classpack will be automatically applied to your ticket purchases with ${hostData.fullName}`}
									</Typography>
								</Box>
							</Box>
						</Box>
					)}
				</Showable>
			</Box>
		</Box>
	);
};
